import React from 'react';

const SingaporeAgenticAI = () => (
  <article className="news-article-body">
    <p className="news-lead">
      In September 2024, Singapore's IMDA published what may be the most detailed
      government-level guidance document on agentic AI anywhere in the world. The
      Agentic AI Framework addresses a genuinely hard problem: how do you govern AI systems
      that make decisions and take actions autonomously, without a human approving every step?
      It's a question every organisation deploying AI agents will eventually need to answer.
    </p>

    <h2>Why agentic AI needs its own framework</h2>
    <p>
      Standard AI governance frameworks - like Singapore's own Model AI Governance Framework -
      were designed for AI systems that take a single input and produce a single output.
      A chatbot that answers a question. A classifier that labels an image. The human is
      always in the loop, reviewing each response before anything happens.
    </p>
    <p>
      Agentic AI breaks that model. An agent that can browse the web, write code, send emails,
      book calendar slots, and interact with APIs might take hundreds of actions before a
      human reviews what happened. The risk profile is completely different.
    </p>

    <h2>Singapore's key recommendations</h2>
    <p>
      The framework doesn't ban autonomous operation - that would make agentic AI useless.
      Instead it defines conditions for safe deployment:
    </p>
    <ul>
      <li><strong>Human oversight triggers:</strong> Define which actions always require human confirmation (irreversible actions, high-value transactions, access to sensitive data)</li>
      <li><strong>Audit trails:</strong> Every agent action should be logged in a way that allows post-hoc review and attribution</li>
      <li><strong>Minimal permissions:</strong> Agents should only have access to the tools and data they actually need for their assigned task</li>
      <li><strong>Failure modes:</strong> Define what the agent should do when uncertain - ask, halt, or take a conservative default action</li>
      <li><strong>Principal hierarchy:</strong> Clearly define which humans are "operators" (configure the agent) and which are "users" (interact with it), and what each can override</li>
    </ul>

    <h2>Industry adoption</h2>
    <p>
      Several Singapore-based financial institutions and government agencies have used the
      framework as the basis for their internal AI governance policies. DBS Bank and
      Singapore Airlines both cited it in their public AI governance disclosures in 2025.
    </p>
    <p>
      The framework has also been referenced by the ASEAN AI Governance Framework working
      group as a model for regional standards - which would extend its influence across
      10 ASEAN member states.
    </p>

    <h2>What's still unresolved</h2>
    <p>
      The framework is honest about open questions. Multi-agent systems - where one AI
      agent delegates to others - create complex accountability chains. If Agent A instructs
      Agent B to take an action that causes harm, who's responsible? The operator who
      deployed Agent A? The vendor who built Agent B? The framework flags this as an
      unsolved problem that requires industry-wide standards.
    </p>

    <div className="news-insight-box">
      <h3>The big lesson</h3>
      <p>
        Singapore's Agentic AI Framework is useful precisely because it doesn't pretend the
        problems are solved. It gives concrete minimum requirements while acknowledging where
        the field still needs to work out the answers. For any organisation deploying AI agents
        in 2025, it's worth reading - not as a compliance checklist, but as a map of the real
        operational risks you need to think about.
      </p>
    </div>
  </article>
);

export default SingaporeAgenticAI;
